import { supabase } from "~/integrations/supabase/client";

export interface FacturaCost {
  costTotal: number; // aggregated WAC cost (net CLP) for the sales_request
  costedLineCount: number; // how many sale lines had a cost movement
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// The cost RPCs (get_sales_request_costs / get_factura_line_costs) were added
// after the last supabase types regeneration, so we call them through a thin
// typed wrapper to keep param/return typing without regenerating the whole
// generated Database type.
type RpcReader = <TRow>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: TRow[] | null; error: { message: string } | null }>;

const rpcRead = supabase.rpc.bind(supabase) as unknown as RpcReader;

export const facturaCostsService = {
  /**
   * Weighted-average cost (net CLP) aggregated per sales_request, sourced from
   * the costing module's `sale` movements. Keyed by `sales_request_id`.
   * Sales requests with no costed movements are simply absent from the map.
   */
  async getCostsBySalesRequest(
    salesRequestIds: string[]
  ): Promise<Map<string, FacturaCost>> {
    const map = new Map<string, FacturaCost>();
    const ids = Array.from(new Set(salesRequestIds.filter(Boolean)));
    if (ids.length === 0) return map;

    for (const part of chunk(ids, 200)) {
      const { data, error } = await rpcRead<{
        sales_request_id: string;
        cost_total_clp: number;
        costed_line_count: number;
      }>("get_sales_request_costs", { p_sales_request_ids: part });
      if (error) throw new Error(error.message);
      for (const row of data ?? []) {
        map.set(row.sales_request_id, {
          costTotal: Number(row.cost_total_clp) || 0,
          costedLineCount: Number(row.costed_line_count) || 0,
        });
      }
    }
    return map;
  },

  /**
   * Per-line WAC cost for credit-note (devolución) lines, from the costing
   * module's `return` movements. Keyed by `${bsale_number}|${cod_prod}|${bsale_detail_id}`.
   * Price-difference (administrative) NCs have no return movement and are absent.
   */
  async getCreditNoteLineCosts(
    bsaleNumbers: string[]
  ): Promise<Map<string, { unitCost: number; costTotal: number }>> {
    const map = new Map<string, { unitCost: number; costTotal: number }>();
    const nums = Array.from(new Set(bsaleNumbers.filter(Boolean)));
    if (nums.length === 0) return map;

    for (const part of chunk(nums, 200)) {
      const { data, error } = await rpcRead<{
        bsale_number: string;
        cod_prod: string;
        bsale_detail_id: number;
        unit_cost_clp: number;
        cost_total_clp: number;
      }>("get_credit_note_line_costs", { p_bsale_numbers: part });
      if (error) throw new Error(error.message);
      for (const row of data ?? []) {
        map.set(`${row.bsale_number}|${row.cod_prod}|${row.bsale_detail_id ?? 0}`, {
          unitCost: Number(row.unit_cost_clp) || 0,
          costTotal: Number(row.cost_total_clp) || 0,
        });
      }
    }
    return map;
  },

  /**
   * Unit WAC cost (net CLP) per `sales_request_item`, for the line-level
   * "Excel Detallado" export. Keyed by item id; absent when not yet costed.
   */
  async getLineCostsByItemIds(itemIds: string[]): Promise<Map<string, number>> {
    const map = new Map<string, number>();
    const ids = Array.from(new Set(itemIds.filter(Boolean)));
    if (ids.length === 0) return map;

    for (const part of chunk(ids, 200)) {
      const { data, error } = await rpcRead<{
        sales_request_item_id: string;
        unit_cost_clp: number;
      }>("get_factura_line_costs", { p_item_ids: part });
      if (error) throw new Error(error.message);
      for (const row of data ?? []) {
        map.set(row.sales_request_item_id, Number(row.unit_cost_clp) || 0);
      }
    }
    return map;
  },
};
